import PropTypes from 'prop-types';
import * as React from 'react';
import { useMemo } from 'react';
import { MANIFEST_FONTS } from '../../../core/manifest/fonts.js';
import { isNonNullish } from '../../../core/utilities/is.utils.js';
import { getSameKeyValueMapForManifest, manifestResolve } from '../../../core/utilities/manifest.utils.js';
import { swanLoaderConfigProps } from '../../contexts/swan-provider/swan-provider.context.js';
import { useSwanHeadConfig } from './head.hook.js';
import { SwanAssetFolders, SwanPathTypeEnum } from '../../../core/types/enums.js';
import { SWAN_BASE_CDN_URL } from '../../../core/constants/domains.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
const SWAN_FONT_KEY_MAP = getSameKeyValueMapForManifest(MANIFEST_FONTS);
var SwanFontNameEnum;
(function(SwanFontNameEnum) {
    // Default Font
    SwanFontNameEnum["Graphik"] = "Graphik";
    SwanFontNameEnum["GraphikBold"] = "GraphikBold";
    SwanFontNameEnum["GraphikMedium"] = "GraphikMedium";
    SwanFontNameEnum["GraphikRegular"] = "GraphikRegular";
    // Special Font
    SwanFontNameEnum["Tiempos"] = "Tiempos";
    SwanFontNameEnum["TiemposRegular"] = "TiemposRegular";
})(SwanFontNameEnum || (SwanFontNameEnum = {}));
_object_spread({
    fontNames: PropTypes.arrayOf(PropTypes.oneOf([
        "Graphik",
        "GraphikBold",
        "GraphikMedium",
        "GraphikRegular",
        "Tiempos",
        "TiemposRegular"
    ]).isRequired),
    renderStyleContentAsChildren: PropTypes.bool
}, swanLoaderConfigProps);
const SwanFonts = ({ fontNames, swanPathType, swanBaseUrl, swanTenant, swanLocale, renderWith: Comp = React.Fragment, renderStyleContentAsChildren })=>{
    const { swanBaseUrl: baseUrl, swanPathType: pathType } = useSwanHeadConfig(swanBaseUrl, swanPathType, swanTenant, swanLocale);
    const [fontPreloadUrls, styleSheetContent] = useMemo(()=>{
        return getSwanFontRenderConfig(fontNames || undefined, pathType, baseUrl);
    }, [
        fontNames,
        pathType,
        baseUrl
    ]);
    return /*#__PURE__*/ React.createElement(Comp, null, fontPreloadUrls.map((url)=>/*#__PURE__*/ React.createElement("link", {
            rel: "preload",
            crossOrigin: "anonymous",
            as: "font",
            href: url,
            key: url
        })), renderStyleContentAsChildren ? /*#__PURE__*/ React.createElement("style", {
        type: "text/css",
        key: "swan-font-config-children"
    }, styleSheetContent) : /*#__PURE__*/ React.createElement("style", {
        type: "text/css",
        key: "swan-font-config-inner-html",
        dangerouslySetInnerHTML: {
            __html: styleSheetContent
        }
    }));
};
function getSwanFontUrl(key, pathType, swanAssetBaseUrl) {
    return manifestResolve(MANIFEST_FONTS, key, SwanAssetFolders.FONTS, pathType, swanAssetBaseUrl);
}
function getSwanFontRenderConfig(fontNames = [
    "Graphik"
], pathType = SwanPathTypeEnum.hashed, swanAssetBaseUrl = SWAN_BASE_CDN_URL) {
    const preLoadUrls = fontNames.flatMap((name)=>getPreLoadUrlsForFontName(name, pathType, swanAssetBaseUrl)).filter(isNonNullish);
    const configString = fontNames.map((name)=>getSwanFontStyleConfig(name, pathType, swanAssetBaseUrl)).join('\n');
    return [
        preLoadUrls,
        configString
    ];
}
function getPreLoadUrlsForFontName(name, pathType, swanAssetBaseUrl) {
    switch(name){
        case "Graphik":
            return [
                "GraphikRegular",
                "GraphikMedium"
            ].flatMap((n)=>getPreLoadUrlsForFontName(n, pathType, swanAssetBaseUrl));
        case "GraphikBold":
            return [
                getSwanFontUrl(SWAN_FONT_KEY_MAP.graphicBold2, pathType, swanAssetBaseUrl)
            ];
        case "GraphikMedium":
            return [
                getSwanFontUrl(SWAN_FONT_KEY_MAP.graphicMedium2, pathType, swanAssetBaseUrl)
            ];
        case "GraphikRegular":
            return [
                getSwanFontUrl(SWAN_FONT_KEY_MAP.graphicRegular2, pathType, swanAssetBaseUrl)
            ];
        case "Tiempos":
            return [
                "TiemposRegular"
            ].flatMap((n)=>getPreLoadUrlsForFontName(n, pathType, swanAssetBaseUrl));
        case "TiemposRegular":
            return [
                getSwanFontUrl(SWAN_FONT_KEY_MAP.tiemposRegular2, pathType, swanAssetBaseUrl)
            ];
        default:
            return [
                null
            ];
    }
}
function getSwanFontStyleConfig(name, pathType, swanAssetBaseUrl) {
    const fontUrlForKey = (key)=>getSwanFontUrl(key, pathType, swanAssetBaseUrl) || '';
    switch(name){
        case "Graphik":
            return [
                "GraphikRegular",
                "GraphikMedium"
            ].map((n)=>getSwanFontStyleConfig(n, pathType, swanAssetBaseUrl)).join('\n');
        case "Tiempos":
            return [
                "TiemposRegular"
            ].map((n)=>getSwanFontStyleConfig(n, pathType, swanAssetBaseUrl)).join('\n');
        case "GraphikBold":
            return `
        @font-face {
          font-family: 'Graphik';
          font-stretch: normal;
          font-style: normal;
          font-weight: 900;
          font-display: swap;
          src: url('${fontUrlForKey(SWAN_FONT_KEY_MAP.graphicBold2)}') format('woff2'),
            url('${fontUrlForKey(SWAN_FONT_KEY_MAP.graphicBold)}') format('woff');
        }`;
        case "GraphikMedium":
            return `
        @font-face {
          font-family: 'Graphik';
          font-stretch: normal;
          font-style: normal;
          font-weight: 700;
          font-display: swap;
          src: url('${fontUrlForKey(SWAN_FONT_KEY_MAP.graphicMedium2)}') format('woff2'),
            url('${fontUrlForKey(SWAN_FONT_KEY_MAP.graphicMedium)}') format('woff');
        }`;
        case "GraphikRegular":
            return `
      @font-face {
        font-family: 'Graphik';
        font-stretch: normal;
        font-style: normal;
        font-weight: 400;
        font-display: swap;
        src: url('${fontUrlForKey(SWAN_FONT_KEY_MAP.graphicRegular2)}') format('woff2'),
          url('${fontUrlForKey(SWAN_FONT_KEY_MAP.graphicRegular)}') format('woff');
      }`;
        case "TiemposRegular":
            return `
      @font-face {
        font-family: 'Tiempos';
        font-stretch: normal;
        font-style: normal;
        font-weight: 400;
        font-display: swap;
        src: url('${fontUrlForKey(SWAN_FONT_KEY_MAP.tiemposRegular2)}') format('woff2');
      }
      `;
        default:
            return ``;
    }
}

export { SWAN_FONT_KEY_MAP, SwanFontNameEnum, SwanFonts, getSwanFontRenderConfig, getSwanFontUrl };
